import * as React from "react";

import { AgentThreadsTable } from "@/Agents/Threads/AgentThreadsTable";
import type { Filter } from "@/shared/foundation/primitives/Table";

import { useDatasetContext } from "../useDatasetContext";

import styles from "./DatasetAgentThreads.module.css";
import { LaunchAgentForDatasetButton } from "./LaunchAgentForDatasetButton";

/**
 * "Agent Threads" tab on the dataset-detail route. Renders the shared
 * :class:`AgentThreadsTable` pinned to the current dataset via a
 * ``subject_id`` scope filter; the backend renders this as a
 * correlated ``EXISTS`` subquery against ``agent_thread_subjects`` so
 * the table shows every thread whose goal target or
 * ``ClientViewingContext`` ever referenced this dataset.
 *
 * The scope is enforced server-side and cannot be widened by the
 * user's filter widget. Even if the user picks ``OR`` match mode for
 * their widget filters, ``composeFilters`` wraps the scope and the
 * user filters in an outer AND so the dataset pin survives.
 *
 * A :class:`LaunchAgentForDatasetButton` rides in the table's
 * ``trailingActions`` slot — same row as the Filter / Columns controls,
 * pushed to the right by the slot's ``margin-left: auto`` — so the
 * affordance sits next to the table chrome rather than on its own
 * empty toolbar row. The button renders nothing when the caller's
 * org has no agents declaring a ``DATASET_ID`` variable.
 *
 * Mounted only when the parent ``DatasetTabs`` is rendered behind the
 * ``release-agents`` feature flag.
 */
export function DatasetAgentThreads() {
  const { datasetId } = useDatasetContext();

  const scope = React.useMemo<Filter[]>(
    () => [
      {
        type: "string",
        field: "subject_id",
        comparator: "EQUALS",
        values: [datasetId],
      },
    ],
    [datasetId],
  );

  return (
    <div className={styles["agent-threads-tab"]}>
      <AgentThreadsTable
        scope={scope}
        noRowsContent="No agent threads referencing this dataset yet."
        trailingActions={<LaunchAgentForDatasetButton datasetId={datasetId} />}
      />
    </div>
  );
}
